import Phaser from 'phaser';

// Game constants
export const GAME_WIDTH = 900;
export const GAME_HEIGHT = 700;

export class BootScene extends Phaser.Scene {
  constructor() {
    super({ key: 'BootScene' });
  }

  preload(): void {
    // Show loading text
    const loadingText = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2, 'Loading...', {
      fontFamily: 'Courier New',
      fontSize: '24px',
      color: '#DEB887'
    }).setOrigin(0.5);

    // Load horse sprites (GIF format - Phaser loads first frame as static image)
    this.load.image('grulla-idle', 'assets/images/grulla1-idle.gif');
    this.load.image('grulla-eat', 'assets/images/grulla1-eat.gif');
    
    // Load paint horse sprites (rare variant)
    this.load.image('paint1-idle', 'assets/images/paint1-idle.gif');
    this.load.image('paint1-eat', 'assets/images/paint1-eat.gif');
    
    // Load burro sprite
    this.load.image('burro', 'assets/images/burro.png');

    // Load background images (900x700) - Day/Night cycle
    this.load.image('pryor_bg0', 'assets/images/pryor_bg0.png');  // Morning
    this.load.image('pryor_bg1', 'assets/images/pryor_bg1.png');  // Midday
    this.load.image('pryor_bg2', 'assets/images/pryor_bg2.png');  // Night

    // Load audio - Music (looping ambient tracks)
    this.load.audio('morning_song', 'assets/audio/Morning-Song.m4a');
    this.load.audio('day_song', 'assets/audio/Day_Song.m4a');
    this.load.audio('night_song', 'assets/audio/Night_Song.m4a');

    // Load audio - SFX
    this.load.audio('horse', 'assets/audio/Horse.m4a');
    this.load.audio('card_use', 'assets/audio/Card_Use.m4a');
    this.load.audio('coin', 'assets/audio/Coin.m4a');
    this.load.audio('year', 'assets/audio/Year.m4a');
    this.load.audio('farrier', 'assets/audio/Farrier.m4a');
    this.load.audio('good_ending', 'assets/audio/Good_Ending.m4a');

    // Handle loading errors gracefully
    this.load.on('loaderror', (file: Phaser.Loader.File) => {
      console.warn(`Failed to load: ${file.key} - creating fallback`);
    });

    this.load.on('complete', () => {
      loadingText.destroy();
      this.createFallbackAssets();
    });
  }

  create(): void {
    this.scene.start('Preloader');
  }

  private createFallbackAssets(): void {
    // Create fallback horse sprites if not loaded
    if (!this.textures.exists('grulla-idle') || this.textures.get('grulla-idle').key === '__MISSING') {
      console.log('Creating fallback grulla-idle sprite');
      this.createFallbackHorseSprite('grulla-idle', false);
    } else {
      console.log('Loaded grulla-idle.gif successfully');
    }
    
    if (!this.textures.exists('grulla-eat') || this.textures.get('grulla-eat').key === '__MISSING') {
      console.log('Creating fallback grulla-eat sprite');
      this.createFallbackHorseSprite('grulla-eat', true);
    } else {
      console.log('Loaded grulla-eat.gif successfully');
    }

    // Create fallback backgrounds if not loaded
    if (!this.textures.exists('pryor_bg0') || this.textures.get('pryor_bg0').key === '__MISSING') {
      this.createFallbackBackground('pryor_bg0', 0xFFE4B5, 0x87CEEB); // Morning - warm
    } else {
      console.log('Loaded pryor_bg0.png (Morning) successfully');
    }
    
    if (!this.textures.exists('pryor_bg1') || this.textures.get('pryor_bg1').key === '__MISSING') {
      this.createFallbackBackground('pryor_bg1', 0x87CEEB, 0x4682B4); // Day - blue sky
    } else {
      console.log('Loaded pryor_bg1.png (Day) successfully');
    }
    
    if (!this.textures.exists('pryor_bg2') || this.textures.get('pryor_bg2').key === '__MISSING') {
      this.createFallbackBackground('pryor_bg2', 0x191970, 0x000033); // Night - dark blue
    } else {
      console.log('Loaded pryor_bg2.png (Night) successfully');
    }

    // Create card back texture
    this.createCardBackTexture();

    // Create UI textures
    this.createUITextures();
  }

  private createFallbackHorseSprite(key: string, isEating: boolean): void {
    const graphics = this.make.graphics({ x: 0, y: 0 });
    const w = 64, h = 64;

    // Horse body (grey base for tinting)
    graphics.fillStyle(0x808080);
    
    if (isEating) {
      // Eating pose - head down
      graphics.fillRect(12, 20, 40, 24); // Body
      graphics.fillRect(8, 38, 12, 8);   // Head (down)
      // Legs
      graphics.fillStyle(0x606060);
      graphics.fillRect(16, 44, 6, 16);
      graphics.fillRect(40, 44, 6, 16);
    } else {
      // Idle pose - head up
      graphics.fillRect(12, 24, 40, 24); // Body
      graphics.fillRect(44, 12, 12, 20); // Head/neck
      // Legs
      graphics.fillStyle(0x606060);
      graphics.fillRect(16, 48, 6, 14);
      graphics.fillRect(40, 48, 6, 14);
    }

    // Mane (darker)
    graphics.fillStyle(0x404040);
    if (isEating) {
      graphics.fillRect(20, 16, 24, 6);
    } else {
      graphics.fillRect(36, 10, 10, 18);
    }

    // Tail
    graphics.fillRect(8, 26, 6, 12);

    // Eye
    graphics.fillStyle(0x000000);
    if (isEating) {
      graphics.fillCircle(12, 40, 2);
    } else {
      graphics.fillCircle(52, 16, 2);
    }

    graphics.generateTexture(key, w, h);
    graphics.destroy();
  }

  private createFallbackBackground(key: string, skyColor1: number, skyColor2: number): void {
    const graphics = this.make.graphics({ x: 0, y: 0 });

    // Sky gradient
    for (let y = 0; y < 350; y++) {
      const ratio = y / 350;
      const r1 = (skyColor1 >> 16) & 0xFF;
      const g1 = (skyColor1 >> 8) & 0xFF;
      const b1 = skyColor1 & 0xFF;
      const r2 = (skyColor2 >> 16) & 0xFF;
      const g2 = (skyColor2 >> 8) & 0xFF;
      const b2 = skyColor2 & 0xFF;
      
      const r = Math.floor(r1 + (r2 - r1) * ratio);
      const g = Math.floor(g1 + (g2 - g1) * ratio);
      const b = Math.floor(b1 + (b2 - b1) * ratio);
      
      graphics.fillStyle((r << 16) | (g << 8) | b);
      graphics.fillRect(0, y, GAME_WIDTH, 1);
    }

    // Mountains
    graphics.fillStyle(0x6B7B8C);
    this.drawMountain(graphics, 100, 350, 200, 150);
    this.drawMountain(graphics, 350, 350, 300, 200);
    this.drawMountain(graphics, 600, 350, 280, 180);
    this.drawMountain(graphics, 800, 350, 220, 140);

    // Snow caps
    graphics.fillStyle(0xFFFFFF);
    this.drawMountain(graphics, 350, 350, 300, 45);
    this.drawMountain(graphics, 600, 350, 280, 35);

    // Grass field
    graphics.fillStyle(0x4A7C23);
    graphics.fillRect(0, 350, GAME_WIDTH, 350);

    // Grass texture
    for (let i = 0; i < 400; i++) {
      const gx = Phaser.Math.Between(0, GAME_WIDTH);
      const gy = Phaser.Math.Between(350, GAME_HEIGHT);
      graphics.fillStyle(Phaser.Math.Between(0, 1) ? 0x5A8C33 : 0x3A6C13);
      graphics.fillRect(gx, gy, 2, 4);
    }

    // Water pond
    graphics.fillStyle(0x4A90B8);
    graphics.fillEllipse(750, 500, 120, 60);
    graphics.fillStyle(0x6AB0D8);
    graphics.fillEllipse(750, 495, 100, 45);

    graphics.generateTexture(key, GAME_WIDTH, GAME_HEIGHT);
    graphics.destroy();
  }

  private createCardBackTexture(): void {
    const graphics = this.make.graphics({ x: 0, y: 0 });
    
    // Card dimensions: 160x220
    graphics.fillStyle(0xDEB887); // Wood color
    graphics.fillRoundedRect(0, 0, 160, 220, 8);
    
    graphics.fillStyle(0xFFF8DC); // Cream
    graphics.fillRoundedRect(4, 4, 152, 212, 6);
    
    // Horse pattern
    graphics.fillStyle(0xDEB887);
    graphics.fillCircle(80, 110, 40);
    
    // Border
    graphics.lineStyle(3, 0x8B5A2B);
    graphics.strokeRoundedRect(2, 2, 156, 216, 7);

    graphics.generateTexture('card_back', 160, 220);
    graphics.destroy();
  }

  private createUITextures(): void {
    // Speaker icon (mute toggle)
    const speakerGraphics = this.make.graphics({ x: 0, y: 0 });
    speakerGraphics.fillStyle(0xFFFFFF);
    speakerGraphics.fillRect(4, 10, 8, 12);
    speakerGraphics.fillTriangle(12, 6, 12, 26, 24, 16);
    // Sound waves
    speakerGraphics.lineStyle(2, 0xFFFFFF);
    speakerGraphics.arc(24, 16, 6, -0.5, 0.5);
    speakerGraphics.arc(24, 16, 10, -0.5, 0.5);
    speakerGraphics.generateTexture('speaker_on', 32, 32);
    speakerGraphics.destroy();

    // Muted speaker
    const mutedGraphics = this.make.graphics({ x: 0, y: 0 });
    mutedGraphics.fillStyle(0x888888);
    mutedGraphics.fillRect(4, 10, 8, 12);
    mutedGraphics.fillTriangle(12, 6, 12, 26, 24, 16);
    // X mark
    mutedGraphics.lineStyle(3, 0xFF6347);
    mutedGraphics.lineBetween(22, 8, 30, 24);
    mutedGraphics.lineBetween(30, 8, 22, 24);
    mutedGraphics.generateTexture('speaker_off', 32, 32);
    mutedGraphics.destroy();

    // Play zone glow
    const glowGraphics = this.make.graphics({ x: 0, y: 0 });
    glowGraphics.fillStyle(0x90EE90, 0.3);
    glowGraphics.fillRoundedRect(0, 0, 800, 120, 12);
    glowGraphics.lineStyle(2, 0x90EE90, 0.6);
    glowGraphics.strokeRoundedRect(2, 2, 796, 116, 10);
    glowGraphics.generateTexture('play_zone_glow', 800, 120);
    glowGraphics.destroy();
  }

  private drawMountain(graphics: Phaser.GameObjects.Graphics, x: number, baseY: number, width: number, height: number): void {
    graphics.beginPath();
    graphics.moveTo(x - width / 2, baseY);
    graphics.lineTo(x, baseY - height);
    graphics.lineTo(x + width / 2, baseY);
    graphics.closePath();
    graphics.fillPath();
  }
}
